import { Box, Radio } from "@mui/material";
import { DragIndicator } from "@mui/icons-material";
import { ChevronDown, ChevronRight, Image, Trash2 } from "lucide-react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LayerNameEditor } from "../../../components/Modal/ModalImageEdit/shared";
import { getDisplaySiblingIDs } from "../instance/instanceGraph";
import { normalizeChildOrder } from "../utils/layerTree";

const SELECTED_BORDER = "#111";
const NESTED_LAYER_INDENT = 2.5;
const CHEVRON_SLOT_WIDTH = 16;

const SortableLayerRow = ({
  item,
  selected,
  hasChildren,
  expanded,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onDelete,
  fallbackName,
  deleteLabel,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: Boolean(disabled) });
  const name = item.node.name || fallbackName;

  return (
    <Box
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      onClick={() => onSelect(item.id)}
      sx={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: 1,
        pr: 1,
        py: 0.875,
        border: selected
          ? `1.5px solid ${SELECTED_BORDER}`
          : "1px solid rgba(0,0,0,0.08)",
        borderRadius: 2,
        bgcolor: "#fff",
        cursor: "pointer",
        textAlign: "left",
        opacity: isDragging ? 0.7 : 1,
        boxShadow: isDragging ? "0 8px 20px rgba(17, 24, 39, 0.12)" : "none",
        zIndex: isDragging ? 2 : 1,
        position: "relative",
      }}
    >
      <Box
        onClick={
          hasChildren
            ? (event) => {
                event.stopPropagation();
                onToggleExpand(item.id);
              }
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: CHEVRON_SLOT_WIDTH,
          color: "rgba(0,0,0,0.4)",
          flexShrink: 0,
        }}
      >
        {hasChildren ? (
          expanded ? (
            <ChevronDown size={14} />
          ) : (
            <ChevronRight size={14} />
          )
        ) : null}
      </Box>
      <Box
        component="span"
        {...(disabled ? {} : attributes)}
        {...(disabled ? {} : listeners)}
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          alignItems: "center",
          color: "rgba(0,0,0,0.28)",
          cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
          touchAction: "none",
          flexShrink: 0,
        }}
      >
        <DragIndicator fontSize="small" />
      </Box>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 1,
          overflow: "hidden",
          bgcolor: "grey.100",
          flexShrink: 0,
          border: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        {item.url ? (
          <Box
            component="img"
            src={item.url}
            alt={name}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
          />
        ) : item.node.type === "shape" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              bgcolor: item.node.shape?.fill || "#46248C",
            }}
          />
        ) : item.node.type === "group" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "text.secondary",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            G
          </Box>
        ) : item.node.type === "image" ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#111",
            }}
          >
            <Image size={16} strokeWidth={2} />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 0.25,
              color: "#111",
              fontSize: 9,
              fontWeight: 700,
              overflow: "hidden",
            }}
          >
            Aa
          </Box>
        )}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {disabled ? (
          <Box
            sx={{
              fontWeight: 700,
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.3,
              fontSize: "0.875rem",
            }}
          >
            {name}
          </Box>
        ) : (
          <LayerNameEditor
            name={name}
            onStartEdit={() => onSelect(item.id)}
            onCommit={(nextName) => {
              const trimmed = String(nextName || "").trim();
              if (trimmed === name) return;
              onRename(item.id, trimmed);
            }}
          />
        )}
      </Box>
      {disabled ? null : (
        <Box
          component="button"
          type="button"
          title={deleteLabel}
          aria-label={deleteLabel}
          onClick={(event) => {
            event.stopPropagation();
            onDelete(item.id);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(0,0,0,0.45)",
            cursor: "pointer",
            flexShrink: 0,
            border: 0,
            bgcolor: "transparent",
            p: 0.25,
            "&:hover": { color: "rgba(0,0,0,0.8)" },
          }}
        >
          <Trash2 size={15} />
        </Box>
      )}
      <Radio
        checked={selected}
        onClick={(event) => event.stopPropagation()}
        onChange={() => onSelect(item.id)}
        size="small"
        disabled={Boolean(disabled)}
        sx={{
          p: 0.5,
          color: "rgba(0,0,0,0.22)",
          "&.Mui-checked": { color: SELECTED_BORDER },
        }}
      />
    </Box>
  );
};

const SiblingList = ({
  parentID,
  nodes,
  nodeLayouts,
  itemsById,
  selectedID,
  expandedIDs,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onReorder,
  onDelete,
  fallbackName,
  deleteLabel,
}) => {
  const siblingIds = getDisplaySiblingIDs({
    nodes,
    nodeLayouts,
    parentID,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const onDragEnd = (event) => {
    if (disabled) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = siblingIds.indexOf(active.id);
    const newIndex = siblingIds.indexOf(over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder({
      parentID,
      orderedDisplayIds: arrayMove(siblingIds, oldIndex, newIndex),
    });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      <SortableContext
        items={siblingIds}
        strategy={verticalListSortingStrategy}
      >
        {siblingIds.map((id, index) => {
          const item = itemsById.get(id);
          if (!item) return null;
          const hasChildren = item.node.type === "group";
          const expanded = Boolean(expandedIDs[id]);
          const childIDs = hasChildren
            ? normalizeChildOrder({
                childOrder: item.node.childOrder,
                children: item.node.children,
              })
            : [];
          return (
            <Box
              key={id}
              sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
              <SortableLayerRow
                item={item}
                selected={selectedID === id}
                hasChildren={hasChildren && childIDs.length > 0}
                expanded={expanded}
                disabled={disabled}
                onSelect={onSelect}
                onRename={onRename}
                onToggleExpand={onToggleExpand}
                onDelete={onDelete}
                fallbackName={fallbackName.replace(
                  "{{index}}",
                  String(index + 1)
                )}
                deleteLabel={deleteLabel}
              />
              {hasChildren && expanded ? (
                <Box
                  sx={{
                    pl: NESTED_LAYER_INDENT,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                  }}
                >
                  <SiblingList
                    parentID={id}
                    nodes={nodes}
                    nodeLayouts={nodeLayouts}
                    itemsById={itemsById}
                    selectedID={selectedID}
                    expandedIDs={expandedIDs}
                    disabled={disabled}
                    onSelect={onSelect}
                    onRename={onRename}
                    onToggleExpand={onToggleExpand}
                    onReorder={onReorder}
                    onDelete={onDelete}
                    fallbackName={fallbackName}
                    deleteLabel={deleteLabel}
                  />
                </Box>
              ) : null}
            </Box>
          );
        })}
      </SortableContext>
    </DndContext>
  );
};

const SortableNestedLayerList = ({
  nodes = {},
  nodeLayouts = {},
  itemsById,
  selectedID,
  expandedIDs,
  disabled,
  onSelect,
  onRename,
  onToggleExpand,
  onReorder,
  onDelete,
  fallbackName,
  deleteLabel,
}) => (
  <SiblingList
    parentID={null}
    nodes={nodes}
    nodeLayouts={nodeLayouts}
    itemsById={itemsById}
    selectedID={selectedID}
    expandedIDs={expandedIDs}
    disabled={disabled}
    onSelect={onSelect}
    onRename={onRename}
    onToggleExpand={onToggleExpand}
    onReorder={onReorder}
    onDelete={onDelete}
    fallbackName={fallbackName}
    deleteLabel={deleteLabel}
  />
);

export default SortableNestedLayerList;
